// Admin Activity Log — audit trail of all admin/sub-admin actions
import React, { useCallback, useEffect, useState } from 'react';
import { Shield, RefreshCw, Filter, Clock } from 'lucide-react';
import { toast } from 'sonner';
import AdminShell from '../../components/admin/AdminShell';
import api from '../../utils/api';

const ACTION_COLORS = {
  approve: 'bg-emerald-500 text-slate-900',
  reject: 'bg-rose-600 text-white',
  assign: 'bg-cyan-500 text-slate-900',
  revoke: 'bg-amber-500 text-slate-900',
  suspend: 'bg-orange-600 text-white',
  reactivate: 'bg-teal-500 text-slate-900',
  create: 'bg-blue-500 text-white',
  delete: 'bg-rose-800 text-white',
  update: 'bg-purple-500 text-white',
};

export default function AdminActivityLog() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ stats: [], total_entries: 0 });
  const [filter, setFilter] = useState({ admin_id: '', target_kind: '', action: '' });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.admin_id) params.admin_id = filter.admin_id;
      if (filter.target_kind) params.target_kind = filter.target_kind;
      if (filter.action) params.action = filter.action;
      const [a, s] = await Promise.all([
        api.get('/admin/activity-log/list', { params: { ...params, limit: 200 } }),
        api.get('/admin/activity-log/stats'),
      ]);
      setRows(a.data.activities || []);
      setStats(s.data || { stats: [], total_entries: 0 });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Load failed'); }
    finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const params = {};
      if (filter.admin_id) params.admin_id = filter.admin_id;
      if (filter.target_kind) params.target_kind = filter.target_kind;
      if (filter.action) params.action = filter.action;
      const r = await api.get('/admin/activity-log/export.csv', { params, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = `activity-log-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('📥 CSV downloaded');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Export failed'); }
  };

  return (
    <AdminShell>
      <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4" data-testid="admin-activity-log">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-black flex items-center gap-2"><Shield size={24} className="text-cyan-400" /> Admin Activity Log</h1>
            <p className="text-xs opacity-70">Complete audit trail · {stats.total_entries} total actions logged</p>
          </div>
          <button onClick={load} data-testid="activity-refresh"
                  className="px-3 py-1.5 rounded-lg bg-slate-700 text-xs font-black flex items-center gap-1">
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <button onClick={exportCsv} data-testid="activity-export-csv"
                  className="px-3 py-1.5 rounded-lg bg-emerald-500 text-slate-900 text-xs font-black flex items-center gap-1">
            📥 Export CSV
          </button>
        </div>

        {/* Stats */}
        {stats.stats.length > 0 && (
          <div className="rounded-xl bg-slate-900/70 border border-slate-800 p-3">
            <div className="text-xs font-black opacity-70 mb-2">Actions by admin</div>
            <div className="flex flex-wrap gap-1">
              {stats.stats.slice(0, 20).map((s, i) => (
                <span key={i} className="px-2 py-0.5 rounded bg-slate-800 text-[10px] font-black" data-testid={`activity-stat-${i}`}>
                  {s.admin?.split('@')[0] || 'unknown'} · <span className="text-cyan-300">{s.action}</span> · {s.count}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Filter */}
        <div className="flex gap-2 flex-wrap">
          <input value={filter.admin_id} onChange={(e) => setFilter({ ...filter, admin_id: e.target.value })}
                 placeholder="Admin ID" data-testid="activity-filter-admin"
                 className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <select value={filter.target_kind} onChange={(e) => setFilter({ ...filter, target_kind: e.target.value })} data-testid="activity-filter-kind"
                  className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs">
            <option value="">All kinds</option>
            <option value="fund_deposit">💰 Fund Deposit</option>
            <option value="at_deposit">🪙 AT Deposit</option>
            <option value="marketplace_order">📦 Order</option>
            <option value="delivery_partner">🛵 Delivery Partner</option>
            <option value="activation_key">🔑 Activation Key</option>
            <option value="support">🆘 Support</option>
          </select>
          <select value={filter.action} onChange={(e) => setFilter({ ...filter, action: e.target.value })} data-testid="activity-filter-action"
                  className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs">
            <option value="">All actions</option>
            <option value="approve">Approve</option>
            <option value="reject">Reject</option>
            <option value="assign">Assign</option>
            <option value="revoke">Revoke</option>
            <option value="suspend">Suspend</option>
            <option value="reactivate">Reactivate</option>
          </select>
        </div>

        {/* Activity rows */}
        {rows.length === 0 && !loading && <div className="text-center py-8 opacity-60 text-xs">No activity yet</div>}
        <div className="space-y-1">
          {rows.map((r, i) => {
            const actionCls = ACTION_COLORS[r.action] || 'bg-slate-600 text-white';
            return (
              <div key={r.id || i} data-testid={`activity-row-${i}`}
                   className="p-2 rounded-lg bg-slate-900/70 border border-slate-800 flex items-center gap-2">
                <span className={`text-[10px] font-black uppercase px-1.5 py-0.5 rounded ${actionCls}`}>{r.action}</span>
                <span className="text-[10px] opacity-70 font-mono">{r.target_kind}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs truncate">
                    <b>{r.admin_email?.split('@')[0] || 'unknown'}</b>
                    <span className="opacity-70"> → </span>
                    <span className="font-mono opacity-80">{(r.target_id || '').slice(0, 20)}</span>
                    {r.details?.reason && <span className="text-rose-300 ml-1">· "{r.details.reason.slice(0, 40)}"</span>}
                    {r.details?.assigned_to && <span className="text-cyan-300 ml-1">→ @{r.details.assigned_to}</span>}
                  </div>
                  <div className="text-[9px] opacity-50 flex items-center gap-1"><Clock size={9} /> {new Date(r.created_at).toLocaleString('en-IN')}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </AdminShell>
  );
}
